import { useMemo, useState } from 'react'

import { MapaRuta, type Parada } from '@/shared/ui/MapaRuta/MapaRuta'

import type { MiTrabajo } from '@/lib/supabase/tipos-bd'
import estilos from './MiRuta.module.css'

/**
 * Tu ruta, por días.
 *
 * El técnico quería ver por dónde le toca andar: sus órdenes puestas en el mapa,
 * en el orden en que las tiene programadas, unidas por una línea. No es un GPS
 * que le diga por dónde ir calle por calle —para eso está «Cómo llegar» de cada
 * tarjeta—: es el panorama del recorrido, para hacerse a la idea y no cruzar el
 * pueblo de ida y vuelta.
 *
 * Empieza en hoy, que es lo que se mira al arrancar la jornada, pero cuando hay
 * trabajo programado para más adelante aparecen pestañas de día —Hoy, Mañana, el
 * resto por su nombre— para planear la semana sin salir de aquí. Y un calendario
 * al lado para saltar a CUALQUIER fecha, no solo a los días que ya tienen
 * trabajo: el técnico pidió poder mirar «¿qué tengo el jueves?» sin depender de
 * que la pestaña de ese día exista.
 *
 * Se pliega: ocupa alto, y hay días de una sola parada en que no dice nada nuevo.
 */
export function MiRuta({ trabajos }: { trabajos: MiTrabajo[] }) {
  // Abierto por defecto: el técnico pidió VER su ruta, no tener que buscarla.
  const [abierto, setAbierto] = useState(true)
  // Qué día se está mirando (clave YYYY-MM-DD). Null = todavía sin elegir, cae
  // en el día por defecto.
  const [diaElegido, setDiaElegido] = useState<string | null>(null)

  // Las órdenes que aún están por hacer (no cerradas), de cualquier día.
  const pendientes = useMemo(
    () => trabajos.filter((t) => t.estado !== 'cerrada' && t.estado !== 'finalizada'),
    [trabajos],
  )

  // Los días que tienen trabajo, en orden, con su etiqueta ya resuelta.
  const dias = useMemo(() => {
    const claves = new Set<string>()
    for (const t of pendientes) {
      if (t.fecha_inicio_programada) claves.add(claveDia(t.fecha_inicio_programada))
    }
    return [...claves].sort().map((clave) => ({ clave, etiqueta: etiquetaDia(clave) }))
  }, [pendientes])

  // Qué día se muestra. Si el técnico eligió uno —por pestaña o por el
  // calendario— manda, aunque ese día no tenga trabajo: entonces se le dice que
  // está libre, en lugar de devolverlo en silencio a hoy y hacerle creer que la
  // fecha no se pudo elegir. Sin elección: hoy si hay ruta hoy, si no el primer
  // día con trabajo, y en último caso hoy a secas.
  const hoy = hoyClave()
  const clavesDias = dias.map((d) => d.clave)
  const seleccion = diaElegido ?? (clavesDias.includes(hoy) ? hoy : (clavesDias[0] ?? hoy))

  // Las órdenes del día seleccionado.
  const delDia = useMemo(
    () =>
      pendientes.filter(
        (t) =>
          t.fecha_inicio_programada && claveDia(t.fecha_inicio_programada) === seleccion,
      ),
    [pendientes, seleccion],
  )

  const paradas = useMemo<Parada[]>(() => {
    return [...delDia]
      .filter((t) => t.latitud != null && t.longitud != null)
      // En el orden en que están programadas: eso es la ruta.
      .sort((a, b) => (a.fecha_inicio_programada ?? '').localeCompare(b.fecha_inicio_programada ?? ''))
      .map((t) => ({
        lat: Number(t.latitud),
        lng: Number(t.longitud),
        etiqueta: t.numero,
        sub: [horaDe(t.fecha_inicio_programada), t.cliente ?? undefined].filter(Boolean).join(' · '),
        activa: t.estado === 'en_ejecucion',
      }))
  }, [delDia])

  // Sin trabajo por hacer en ningún día: no hay nada que mostrar.
  if (pendientes.length === 0) return null

  // El día elegido no tiene trabajo: está libre. Se dice con todas las letras,
  // que es distinto de «hay trabajo pero sin punto en el mapa».
  const libre = delDia.length === 0
  // Hay trabajo pero sin punto en el mapa (direcciones sin geocodificar aún):
  // se dice, para que el técnico no crea que la app está fallando.
  const sinUbicar = !libre && paradas.length === 0

  return (
    <section className={estilos.zona}>
      <button
        type="button"
        className={estilos.cabecera}
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
      >
        <span className={estilos.titulo}>Tu ruta {tituloDia(seleccion, hoy)}</span>
        <span className={estilos.cuantas}>
          {libre
            ? 'libre'
            : sinUbicar
              ? `${delDia.length} ${delDia.length === 1 ? 'orden' : 'órdenes'}`
              : `${paradas.length} ${paradas.length === 1 ? 'parada' : 'paradas'}`}
        </span>
        <span className={estilos.flecha} aria-hidden="true">
          {abierto ? '▾' : '▸'}
        </span>
      </button>

      {abierto && (
        <div className={estilos.cuerpo}>
          {/* Las pestañas de día solo cuando hay más de uno: con un solo día
              serían un adorno que ocupa y no elige nada. El calendario, en
              cambio, va SIEMPRE: es el que deja saltar a una fecha que todavía
              no tiene trabajo —«¿qué me toca el jueves?»—, que es justo lo que
              las pestañas no pueden ofrecer porque solo listan lo ya programado. */}
          <div className={estilos.selector}>
            {dias.length > 1 && (
              <div className={estilos.dias} role="tablist" aria-label="Día de la ruta">
                {dias.map((d) => (
                  <button
                    key={d.clave}
                    type="button"
                    role="tab"
                    aria-selected={d.clave === seleccion}
                    className={`${estilos.dia} ${d.clave === seleccion ? estilos.diaActivo : ''}`}
                    onClick={() => setDiaElegido(d.clave)}
                  >
                    {d.etiqueta}
                  </button>
                ))}
              </div>
            )}

            <label className={estilos.calendario}>
              <span className={estilos.calendarioTexto}>Ir a una fecha</span>
              <input
                type="date"
                className={estilos.fecha}
                value={seleccion}
                onChange={(e) => setDiaElegido(e.target.value || null)}
              />
            </label>
          </div>

          {libre ? (
            <p className={estilos.sinUbicar}>
              No tienes trabajo programado para {tituloDia(seleccion, hoy).replace(/^de[l]? /, '')}.
            </p>
          ) : sinUbicar ? (
            <p className={estilos.sinUbicar}>
              Estas órdenes todavía no tienen ubicación en el mapa. Cuando la oficina las ubique por
              la dirección, aparecen aquí.
            </p>
          ) : (
            <MapaRuta paradas={paradas} altura={280} />
          )}
        </div>
      )}
    </section>
  )
}

/** Hora local (Colombia) en formato 7:00 a. m., o vacío si no está programada. */
function horaDe(fecha: string | null): string {
  if (!fecha) return ''
  try {
    return new Intl.DateTimeFormat('es-CO', {
      timeZone: 'America/Bogota',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(fecha))
  } catch {
    return ''
  }
}

/** El día del calendario (hora de Colombia) de una fecha ISO: «2026-09-22». */
function claveDia(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Bogota' })
}

/** El día de hoy en Colombia, en la misma clave que devuelve `claveDia`. */
function hoyClave(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' })
}

/** La pestaña de un día: «Hoy», «Mañana» o, más allá, «mar 22». */
function etiquetaDia(clave: string): string {
  const hoy = hoyClave()
  const manana = new Date(Date.now() + 86_400_000).toLocaleDateString('en-CA', {
    timeZone: 'America/Bogota',
  })
  if (clave === hoy) return 'Hoy'
  if (clave === manana) return 'Mañana'
  // Mediodía local para nombrar el día sin que el huso lo corra al anterior.
  return new Date(clave + 'T12:00:00').toLocaleDateString('es-CO', {
    weekday: 'short',
    day: 'numeric',
  })
}

/** El día en el título: «de hoy», «de mañana» o «del viernes 22». */
function tituloDia(clave: string | null, hoy: string): string {
  if (!clave) return 'de hoy'
  const manana = new Date(Date.now() + 86_400_000).toLocaleDateString('en-CA', {
    timeZone: 'America/Bogota',
  })
  if (clave === hoy) return 'de hoy'
  if (clave === manana) return 'de mañana'
  return (
    'del ' +
    new Date(clave + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric' })
  )
}
